// Converts Bubble-style BBCode to HTML for content migrated from the old
// Bubble app. Only transforms a known whitelist of tags — unrecognized
// bracketed text (e.g. Bubble dynamic-data placeholders like "[First Name]")
// is left as-is.

const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

// Quick detector — skip the conversion pipeline entirely when there's no
// BBCode present.
export function looksLikeBbcode(input) {
  if (!input) return false
  return /\[(?:b|i|u|url|img|ol|ul|li|center|code|quote|sup|sub|color)[\]=\s/]/i.test(
    input
  )
}

export function bbcodeToHtml(input) {
  if (!input) return ""
  let s = String(input)

  // Simple pair tags
  const pairs = [
    ["b", "strong"],
    ["i", "em"],
    ["u", "u"],
    ["sup", "sup"],
    ["sub", "sub"],
    ["code", "code"],
    ["quote", "blockquote"],
  ]
  for (const [bb, html] of pairs) {
    const re = new RegExp(`\\[${bb}\\]([\\s\\S]*?)\\[/${bb}\\]`, "gi")
    s = s.replace(re, `<${html}>$1</${html}>`)
  }

  // [center]...[/center] -> <div class="text-center">...</div>
  s = s.replace(
    /\[center\]([\s\S]*?)\[\/center\]/gi,
    '<div class="text-center">$1</div>'
  )

  // [color=...]...[/color] -> strip the color, keep the text
  s = s.replace(/\[color=[^\]]*\]([\s\S]*?)\[\/color\]/gi, "$1")

  // [url=HREF]TEXT[/url]
  s = s.replace(
    /\[url=([^\]]+)\]([\s\S]*?)\[\/url\]/gi,
    (_m, href, text) =>
      `<a href="${escapeHtml(href.trim())}" target="_blank" rel="noreferrer noopener">${text}</a>`
  )
  // [url]HREF[/url]
  s = s.replace(/\[url\]([\s\S]*?)\[\/url\]/gi, (_m, href) => {
    const h = escapeHtml(href.trim())
    return `<a href="${h}" target="_blank" rel="noreferrer noopener">${h}</a>`
  })

  // [img]SRC[/img] or [img width=404px height=200]SRC[/img]
  s = s.replace(
    /\[img(\s+[^\]]*)?\]([\s\S]*?)\[\/img\]/gi,
    (_m, attrs, src) => {
      const raw = (src || "").trim()
      // Bubble emits protocol-relative URLs (//cdn.bubble.io/...) — they
      // work in HTML but browsers can't resolve them in preview contexts
      // without a base, so normalize to https:
      const url = raw.startsWith("//") ? `https:${raw}` : raw
      const widthMatch = attrs?.match(/\bwidth\s*=\s*"?([\d]+)(?:px)?"?/i)
      const heightMatch = attrs?.match(/\bheight\s*=\s*"?([\d]+)(?:px)?"?/i)
      const width = widthMatch ? ` width="${widthMatch[1]}"` : ""
      const height = heightMatch ? ` height="${heightMatch[1]}"` : ""
      return `<img src="${escapeHtml(url)}" alt=""${width}${height} />`
    }
  )

  // Lists: [ol]...[/ol], [ul]...[/ul], [li ...]...[/li]
  s = s.replace(/\[ol\]([\s\S]*?)\[\/ol\]/gi, "<ol>$1</ol>")
  s = s.replace(/\[ul\]([\s\S]*?)\[\/ul\]/gi, "<ul>$1</ul>")
  // [li] or [li indent=0 align=left] -> <li>; we drop attributes because
  // Bubble's indent/align don't map cleanly and the allowed-attr list
  // would need to grow to pass them through safely.
  s = s.replace(/\[li(?:\s+[^\]]*)?\]([\s\S]*?)\[\/li\]/gi, "<li>$1</li>")

  // Strip unknown wrapper tags Bubble emits around groups (e.g. [ml]...[/ml])
  // but ONLY if both open+close are present. We do this last so known tags
  // have already been converted.
  s = s.replace(/\[(ml|size|font|left|right)(?:=[^\]]*)?\]([\s\S]*?)\[\/\1\]/gi, "$2")

  // Strip orphan open/close tags Bubble occasionally leaves behind
  // (e.g. trailing [/color] when a wrapper was split). Only known BBCode tags
  // are targeted so that Bubble dynamic-data placeholders like
  // "[First Name]" are preserved.
  s = s.replace(/\[\/?(?:color|ml|size|font|left|right|center|url|img|ol|ul|li|b|i|u|sup|sub|code|quote)(?:=[^\]]*)?\]/gi, "")

  return s
}
